/**
 * VideoCallModal.js - WebRTC Video/Voice Call Component
 * Handles peer-to-peer video and voice calls with signaling via WebSocket
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Phone, PhoneOff, Video, VideoOff, Mic, MicOff, 
  Volume2, VolumeX, Maximize2, Minimize2, X, User,
  PhoneIncoming, PhoneOutgoing, Clock, AlertCircle
} from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const ICE_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
  { urls: 'stun:stun2.l.google.com:19302' },
];

const VideoCallModal = ({ 
  isOpen, 
  onClose, 
  callData,
  currentUser,
  websocket,
  onCallEnd 
}) => {
  // Call state
  const [callStatus, setCallStatus] = useState('initializing'); // initializing, ringing, connecting, connected, ended
  const [callType, setCallType] = useState(callData?.callType || 'video');
  const [callDuration, setCallDuration] = useState(0);
  const [error, setError] = useState(null);
  
  // Media state
  const [isVideoEnabled, setIsVideoEnabled] = useState(callType === 'video');
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [isRemoteAudioEnabled, setIsRemoteAudioEnabled] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // WebRTC refs
  const peerConnection = useRef(null);
  const localStream = useRef(null);
  const remoteStream = useRef(null);
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const durationInterval = useRef(null);
  
  // Call info
  const isIncoming = callData?.direction === 'incoming';
  const otherParty = callData?.otherParty || {};

  // Initialize media stream
  const initializeMedia = useCallback(async () => {
    try {
      const constraints = {
        audio: true,
        video: callType === 'video' ? { 
          width: { ideal: 1280 }, 
          height: { ideal: 720 },
          facingMode: 'user'
        } : false
      };
      
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      localStream.current = stream;
      
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }
      
      return stream;
    } catch (err) {
      console.error('Media access error:', err);
      if (err.name === 'NotAllowedError') {
        setError('Camera/microphone access denied. Please allow access in your browser settings.');
      } else if (err.name === 'NotFoundError') {
        setError('No camera/microphone found. Please connect a device.');
      } else {
        setError(`Media error: ${err.message}`);
      }
      throw err;
    }
  }, [callType]);

  // Create peer connection
  const createPeerConnection = useCallback(() => {
    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
    
    // Add local tracks
    if (localStream.current) {
      localStream.current.getTracks().forEach(track => {
        pc.addTrack(track, localStream.current);
      });
    }
    
    // Handle remote stream
    pc.ontrack = (event) => {
      if (!remoteStream.current) {
        remoteStream.current = new MediaStream();
      }
      remoteStream.current.addTrack(event.track);
      
      if (remoteVideoRef.current) {
        remoteVideoRef.current.srcObject = remoteStream.current;
      }
    };
    
    // Handle ICE candidates
    pc.onicecandidate = (event) => {
      if (event.candidate && websocket) {
        websocket.send(JSON.stringify({
          type: 'ice_candidate',
          call_id: callData?.callId,
          target_id: otherParty.id,
          candidate: event.candidate
        }));
      }
    };
    
    // Handle connection state changes
    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'connected') {
        setCallStatus('connected');
        startDurationTimer();
      } else if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
        handleCallEnd('connection_failed');
      }
    };
    
    pc.oniceconnectionstatechange = () => {
    };
    
    peerConnection.current = pc;
    return pc;
  }, [websocket, callData, otherParty.id]);

  // Start duration timer
  const startDurationTimer = useCallback(() => {
    if (durationInterval.current) clearInterval(durationInterval.current);
    setCallDuration(0);
    durationInterval.current = setInterval(() => {
      setCallDuration(d => d + 1);
    }, 1000);
  }, []);

  // Format duration
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Make outgoing call
  const makeCall = useCallback(async () => {
    try {
      setCallStatus('ringing');
      
      await initializeMedia();
      const pc = createPeerConnection();
      
      // Create offer
      const offer = await pc.createOffer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: callType === 'video'
      });
      await pc.setLocalDescription(offer);
      
      // Send offer via WebSocket
      if (websocket) {
        websocket.send(JSON.stringify({
          type: 'call_offer',
          call_id: callData?.callId,
          recipient_id: otherParty.id,
          call_type: callType,
          offer: offer
        }));
      }
      
      setCallStatus('ringing');
    } catch (err) {
      console.error('Make call error:', err);
      handleCallEnd('failed');
    }
  }, [initializeMedia, createPeerConnection, websocket, callData, otherParty.id, callType]);

  // Answer incoming call
  const answerCall = useCallback(async () => {
    try {
      setCallStatus('connecting');
      
      await initializeMedia();
      const pc = createPeerConnection();
      
      // Set remote description from offer
      if (callData?.offer) {
        await pc.setRemoteDescription(new RTCSessionDescription(callData.offer));
        
        // Create answer
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        
        // Send answer via WebSocket
        if (websocket) {
          websocket.send(JSON.stringify({
            type: 'call_answer',
            call_id: callData.callId,
            caller_id: otherParty.id,
            answer: answer
          }));
        }
      }
    } catch (err) {
      console.error('Answer call error:', err);
      handleCallEnd('failed');
    }
  }, [initializeMedia, createPeerConnection, websocket, callData, otherParty.id]);

  // Handle incoming answer
  const handleAnswer = useCallback(async (answer) => {
    try {
      if (peerConnection.current) {
        await peerConnection.current.setRemoteDescription(new RTCSessionDescription(answer));
        setCallStatus('connecting');
      }
    } catch (err) {
      console.error('Handle answer error:', err);
    }
  }, []);

  // Handle ICE candidate
  const handleIceCandidate = useCallback(async (candidate) => {
    try {
      if (peerConnection.current && candidate) {
        await peerConnection.current.addIceCandidate(new RTCIceCandidate(candidate));
      }
    } catch (err) {
      console.error('ICE candidate error:', err);
    }
  }, []);

  // End call
  const handleCallEnd = useCallback((reason = 'ended') => {
    // Stop all tracks
    if (localStream.current) {
      localStream.current.getTracks().forEach(track => track.stop());
    }
    
    // Close peer connection
    if (peerConnection.current) {
      peerConnection.current.close();
    }
    
    // Clear timer
    if (durationInterval.current) {
      clearInterval(durationInterval.current);
    }
    
    // Notify other party via WebSocket
    if (websocket && otherParty.id) {
      websocket.send(JSON.stringify({
        type: 'call_end',
        call_id: callData?.callId,
        target_id: otherParty.id,
        reason: reason
      }));
    }
    
    setCallStatus('ended');
    
    if (onCallEnd) {
      onCallEnd({ callId: callData?.callId, duration: callDuration, reason });
    }
    
    setTimeout(() => {
      if (onClose) onClose();
    }, 1500);
  }, [websocket, otherParty.id, callData, callDuration, onCallEnd, onClose]);

  // Reject call
  const rejectCall = useCallback(() => {
    if (websocket && callData?.callId) {
      websocket.send(JSON.stringify({
        type: 'call_reject',
        call_id: callData.callId,
        caller_id: otherParty.id
      }));
    }
    if (onClose) onClose();
  }, [websocket, callData, otherParty.id, onClose]);

  // Toggle video
  const toggleVideo = useCallback(() => {
    if (localStream.current) {
      const videoTrack = localStream.current.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setIsVideoEnabled(videoTrack.enabled);
      }
    }
  }, []);

  // Toggle audio
  const toggleAudio = useCallback(() => {
    if (localStream.current) {
      const audioTrack = localStream.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsAudioEnabled(audioTrack.enabled);
      }
    }
  }, []);

  // Toggle remote audio
  const toggleRemoteAudio = useCallback(() => {
    if (remoteVideoRef.current) {
      remoteVideoRef.current.muted = !remoteVideoRef.current.muted;
      setIsRemoteAudioEnabled(!remoteVideoRef.current.muted);
    }
  }, []);

  // Handle WebSocket messages
  useEffect(() => {
    if (!websocket) return;
    
    const handleMessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        if (data.call_id !== callData?.callId) return;
        
        switch (data.type) {
          case 'call_answer':
            handleAnswer(data.answer);
            break;
          case 'ice_candidate':
            handleIceCandidate(data.candidate);
            break;
          case 'call_ended':
            setCallStatus('ended');
            toast.info(`Call ended by ${otherParty.name || 'other party'}`);
            setTimeout(() => onClose?.(), 1500);
            break;
          case 'call_rejected':
            setCallStatus('ended');
            toast.info('Call was declined');
            setTimeout(() => onClose?.(), 1500);
            break;
          case 'call_busy':
            setCallStatus('ended');
            toast.info(`${otherParty.name || 'User'} is busy`);
            setTimeout(() => onClose?.(), 1500);
            break;
          default:
            break;
        }
      } catch (err) {
        console.error('WebSocket message error:', err);
      }
    };
    
    websocket.addEventListener('message', handleMessage);
    return () => websocket.removeEventListener('message', handleMessage);
  }, [websocket, callData, handleAnswer, handleIceCandidate, otherParty.name, onClose]);

  // Initialize call on mount
  useEffect(() => {
    if (!isOpen) return;
    
    if (isIncoming) {
      setCallStatus('ringing');
    } else {
      makeCall();
    }
    
    return () => {
      if (durationInterval.current) {
        clearInterval(durationInterval.current);
      }
    };
  }, [isOpen, isIncoming, makeCall]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (localStream.current) {
        localStream.current.getTracks().forEach(track => track.stop());
      }
      if (peerConnection.current) {
        peerConnection.current.close();
      }
      if (durationInterval.current) {
        clearInterval(durationInterval.current);
      }
    };
  }, []);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-[100]" data-testid="video-call-modal">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          className={`relative bg-gray-900 rounded-2xl overflow-hidden ${
            isFullscreen ? 'w-full h-full' : 'w-full max-w-4xl h-[80vh]'
          }`}
        >
          {/* Remote Video (Large) */}
          <div className="absolute inset-0 bg-gray-800">
            {callType === 'video' && callStatus === 'connected' ? (
              <video
                ref={remoteVideoRef}
                autoPlay
                playsInline
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <div className="text-center">
                  <div className="w-32 h-32 rounded-full bg-[#D4AF37]/20 flex items-center justify-center mx-auto mb-4">
                    <User className="w-16 h-16 text-[#D4AF37]" />
                  </div>
                  <p className="text-white text-xl font-semibold">{otherParty.name || 'Unknown'}</p>
                  <p className="text-gray-400 capitalize">{otherParty.role || ''}</p>
                </div>
              </div>
            )}
          </div>

          {/* Local Video (Small - Picture in Picture) */}
          {callType === 'video' && (
            <div className="absolute bottom-24 right-4 w-48 h-36 rounded-xl overflow-hidden shadow-lg border-2 border-gray-700">
              <video
                ref={localVideoRef}
                autoPlay
                playsInline
                muted
                className={`w-full h-full object-cover ${!isVideoEnabled ? 'hidden' : ''}`}
              />
              {!isVideoEnabled && (
                <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                  <VideoOff className="w-8 h-8 text-gray-500" />
                </div>
              )}
            </div>
          )}

          {/* Status Header */}
          <div className="absolute top-0 left-0 right-0 p-4 bg-gradient-to-b from-black/50 to-transparent">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white font-semibold text-lg">{otherParty.name || 'Unknown'}</p>
                <div className="flex items-center gap-2 text-gray-300 text-sm">
                  {callStatus === 'ringing' && (
                    <>
                      {isIncoming ? <PhoneIncoming className="w-4 h-4" /> : <PhoneOutgoing className="w-4 h-4" />}
                      <span>{isIncoming ? 'Incoming call...' : 'Ringing...'}</span>
                    </>
                  )}
                  {callStatus === 'connecting' && (
                    <>
                      <Clock className="w-4 h-4 animate-pulse" />
                      <span>Connecting...</span>
                    </>
                  )}
                  {callStatus === 'connected' && (
                    <>
                      <Clock className="w-4 h-4" />
                      <span>{formatDuration(callDuration)}</span>
                    </>
                  )}
                  {callStatus === 'ended' && (
                    <>
                      <PhoneOff className="w-4 h-4" />
                      <span>Call ended</span>
                    </>
                  )}
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsFullscreen(!isFullscreen)}
                  className="text-white hover:bg-white/20"
                >
                  {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
                </Button>
              </div>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="absolute top-20 left-4 right-4">
              <div className="bg-red-500/90 text-white p-3 rounded-lg flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                <span>{error}</span>
              </div>
            </div>
          )}

          {/* Incoming Call Actions */}
          {isIncoming && callStatus === 'ringing' && (
            <div className="absolute bottom-8 left-0 right-0 flex justify-center gap-8">
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={rejectCall}
                className="w-16 h-16 rounded-full bg-red-500 flex items-center justify-center shadow-lg"
                data-testid="reject-call-btn"
              >
                <PhoneOff className="w-8 h-8 text-white" />
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={answerCall}
                className="w-16 h-16 rounded-full bg-green-500 flex items-center justify-center shadow-lg"
                data-testid="accept-call-btn"
              >
                <Phone className="w-8 h-8 text-white" />
              </motion.button>
            </div>
          )}

          {/* In-Call Controls */}
          {(callStatus === 'connected' || callStatus === 'connecting' || (!isIncoming && callStatus === 'ringing')) && (
            <div className="absolute bottom-8 left-0 right-0">
              <div className="flex justify-center items-center gap-4">
                {/* Toggle Audio */}
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={toggleAudio}
                  className={`w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-colors ${
                    isAudioEnabled ? 'bg-gray-700 hover:bg-gray-600' : 'bg-red-500'
                  }`}
                  data-testid="toggle-mic-btn"
                >
                  {isAudioEnabled ? <Mic className="w-6 h-6 text-white" /> : <MicOff className="w-6 h-6 text-white" />}
                </motion.button>
                
                {/* Toggle Video (if video call) */}
                {callType === 'video' && (
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={toggleVideo}
                    className={`w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-colors ${
                      isVideoEnabled ? 'bg-gray-700 hover:bg-gray-600' : 'bg-red-500'
                    }`}
                    data-testid="toggle-video-btn"
                  >
                    {isVideoEnabled ? <Video className="w-6 h-6 text-white" /> : <VideoOff className="w-6 h-6 text-white" />}
                  </motion.button>
                )}
                
                {/* End Call */}
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => handleCallEnd('ended')}
                  className="w-16 h-16 rounded-full bg-red-500 flex items-center justify-center shadow-lg"
                  data-testid="end-call-btn"
                >
                  <PhoneOff className="w-8 h-8 text-white" />
                </motion.button>
                
                {/* Toggle Remote Audio */}
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={toggleRemoteAudio}
                  className={`w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-colors ${
                    isRemoteAudioEnabled ? 'bg-gray-700 hover:bg-gray-600' : 'bg-amber-500'
                  }`}
                  data-testid="toggle-speaker-btn"
                >
                  {isRemoteAudioEnabled ? <Volume2 className="w-6 h-6 text-white" /> : <VolumeX className="w-6 h-6 text-white" />}
                </motion.button>
              </div>
            </div>
          )}

          {/* Close button (when ended) */}
          {callStatus === 'ended' && (
            <div className="absolute bottom-8 left-0 right-0 flex justify-center">
              <Button onClick={onClose} className="bg-gray-700 hover:bg-gray-600 text-white">
                <X className="w-5 h-5 mr-2" />
                Close
              </Button>
            </div>
          )}
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default VideoCallModal;
